import { Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { navigateRoute, useAppDispatch, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { type MouseEvent, useCallback, useState } from "react";

import { Col, Row } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import PreventManageSubscriptionModal from "@/components/PreventManageSubscriptionModal";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import { isExpiredPlanState, resolveStatusTextColor } from "@/constants/planTierColors";
import { useMediaStore } from "@/hooks";
import useManageSubscription from "@/hooks/useManageSubscription";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";
import { cn } from "@/utils";

import useCurrentPlan from "../hooks/useCurrentPlan";

const LINKABLE_PLAN_CARD_CLASS_NAME = "block no-underline text-inherit cursor-pointer transition-opacity hover:opacity-95";

type CurrentPlanCardProps = {
  plan: ReturnType<typeof useCurrentPlan>;
};

const MUTED_TIER_ICON_BADGE_CLASS_NAME = "bg-gray-5 border border-solid border-gray-8";

type ResolveTierIconBadgeClassNameParams = {
  isExpiredPlan: boolean;
  isMutedTierIcon: boolean;
  iconBadgeFrom: string;
  iconBadgeTo: string;
};

const resolveTierIconBadgeClassName = ({
  isExpiredPlan,
  isMutedTierIcon,
  iconBadgeFrom,
  iconBadgeTo,
}: ResolveTierIconBadgeClassNameParams): string => {
  if (!isMutedTierIcon) {
    return cn("bg-gradient-to-br", iconBadgeFrom, iconBadgeTo);
  }

  if (isExpiredPlan) {
    return cn(iconBadgeFrom, "border border-solid", iconBadgeTo);
  }

  return MUTED_TIER_ICON_BADGE_CLASS_NAME;
};

type CurrentPlanManageAffordanceProps = {
  shouldShowManage: boolean;
  manageLabel: string;
};

const CurrentPlanManageAffordance = ({ shouldShowManage, manageLabel }: CurrentPlanManageAffordanceProps) => {
  const isMobile = useMediaStore((state) => state.isMobile);

  if (!shouldShowManage) {
    return null;
  }

  if (isMobile) {
    return <Icon icon={IconVariants.CHEVRON_RIGHT} size={IconSize.SMALL} className="shrink-0 text-gray-12" />;
  }

  return (
    <div
      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-default border border-gray-6 bg-gray-12 px-[13px] h-[36px] text-white dark:bg-gray-5"
      data-testid="CurrentPlan-ManageButton"
    >
      <Text variant={TextVariants.LABEL_SMALL_MEDIUM}>{manageLabel}</Text>
      <Icon icon={IconVariants.CHEVRON_RIGHT} size={IconSize.XSMALL} />
    </div>
  );
};

const CurrentPlanCard = ({ plan }: CurrentPlanCardProps) => {
  const isExpiredPlan = isExpiredPlanState(plan.planState);
  const isFreePlan = plan.shouldShowUpgrade;
  const isMutedTierIcon = isExpiredPlan || isFreePlan;
  const statusTextColor = resolveStatusTextColor(plan.colors);
  const tierIconBadgeClassName = resolveTierIconBadgeClassName({
    isExpiredPlan,
    isMutedTierIcon,
    iconBadgeFrom: plan.colors.iconBadgeFrom,
    iconBadgeTo: plan.colors.iconBadgeTo,
  });

  return (
    <Row
      align="center"
      className={cn("gap-md rounded-xl p-md w-full border border-solid border-gray-6", plan.colors.background)}
      data-testid="CurrentPlan-PlanCard"
    >
      <div className={cn("flex shrink-0 items-center justify-center rounded-xl size-11", tierIconBadgeClassName)} data-testid="CurrentPlan-TierIcon">
        <Icon icon={plan.colors.icon} size={IconSize.MEDIUM} className={isMutedTierIcon ? "text-gray-11" : "text-white"} />
      </div>
      <Col className="flex-1 min-w-0 gap-0.5">
        <Row align="center" className="gap-2 flex-wrap">
          <Text variant={TextVariants.LABEL_LARGE_MEDIUM} className="text-gray-12">
            {plan.planName}
          </Text>
          {plan.shouldShowUpgrade ? (
            <span
              className="inline-flex items-center rounded px-1.5 py-0.5 bg-gray-6 border border-solid border-gray-8"
              data-testid="CurrentPlan-FreeBadge"
            >
              <Text variant={TextVariants.LABEL_XSMALL_MEDIUM} className="text-gray-11 uppercase tracking-wide">
                <Trans i18nKey="user:profile.currentPlan.badge.free" />
              </Text>
            </span>
          ) : null}
        </Row>
        <Row align="center" className="gap-1 flex-wrap">
          {plan.statusIcon ? <Icon icon={plan.statusIcon} size={IconSize.XSMALL} className={statusTextColor} /> : null}
          <Text variant={TextVariants.LABEL_SMALL_MEDIUM} className={statusTextColor}>
            {plan.statusLabel}
          </Text>
          {plan.periodLabel ? (
            <>
              <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                ·
              </Text>
              <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                {plan.periodLabel}
              </Text>
            </>
          ) : null}
        </Row>
      </Col>
      {plan.shouldShowUpgrade ? (
        <Row align="center" className="shrink-0 gap-1 text-blue-10" data-testid="CurrentPlan-UpgradeButton">
          <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM} className="text-blue-10">
            {plan.upgradeLabel}
          </Text>
          <Icon icon={IconVariants.CHEVRON_RIGHT} size={IconSize.SMALL} className="text-blue-10" />
        </Row>
      ) : null}
      <CurrentPlanManageAffordance shouldShowManage={plan.shouldShowManage} manageLabel={plan.manageLabel} />
    </Row>
  );
};

const CurrentPlan = () => {
  const plan = useCurrentPlan();
  const { manageLinkHref, isExternalManageLink, isMobileManagedSubscription } = useManageSubscription();
  const [stateIsPreventModalOpen, setStateIsPreventModalOpen] = useState(false);
  const { isProfileModified } = useUserInfo();
  const { setToShowUnsavedProfileChangesWarning } = useUserSettings();
  const dispatch = useAppDispatch();
  const localizePath = useLocalizedPathname();

  const handleOpenPreventModal = useCallback(() => {
    setStateIsPreventModalOpen(true);
  }, []);

  const handleClosePreventModal = useCallback(() => {
    setStateIsPreventModalOpen(false);
  }, []);

  const handleUpgradeClick = useCallback(
    (e: MouseEvent) => {
      if (isProfileModified) {
        e.preventDefault();
        dispatch(navigateRoute(localizePath(ROUTES.subscribe)));
        setToShowUnsavedProfileChangesWarning(true);
      }
    },
    [isProfileModified, dispatch, localizePath, setToShowUnsavedProfileChangesWarning],
  );

  const handleManageClick = useCallback(
    (e: MouseEvent) => {
      // Only guard internal manage links. External links open in a new tab,
      // preserving the profile page and its unsaved changes.
      if (isProfileModified && !isExternalManageLink && manageLinkHref) {
        e.preventDefault();
        dispatch(navigateRoute(localizePath(manageLinkHref)));
        setToShowUnsavedProfileChangesWarning(true);
      }
    },
    [isProfileModified, isExternalManageLink, manageLinkHref, dispatch, localizePath, setToShowUnsavedProfileChangesWarning],
  );

  let planCardElement = <CurrentPlanCard plan={plan} />;

  if (plan.shouldShowUpgrade) {
    planCardElement = (
      <LocalizedLink
        to={ROUTES.subscribe}
        className={LINKABLE_PLAN_CARD_CLASS_NAME}
        data-testid="CurrentPlan-PlanCardLink"
        onClick={handleUpgradeClick}
      >
        <CurrentPlanCard plan={plan} />
      </LocalizedLink>
    );
  } else if (plan.shouldShowManage && isMobileManagedSubscription) {
    planCardElement = (
      <button
        type="button"
        onClick={handleOpenPreventModal}
        className={cn(LINKABLE_PLAN_CARD_CLASS_NAME, "bg-transparent")}
        data-testid="CurrentPlan-PlanCardLink"
      >
        <CurrentPlanCard plan={plan} />
      </button>
    );
  } else if (plan.shouldShowManage && manageLinkHref) {
    planCardElement = (
      <LocalizedLink
        to={manageLinkHref}
        target={isExternalManageLink ? "_blank" : undefined}
        rel={isExternalManageLink ? "noopener noreferrer" : undefined}
        className={LINKABLE_PLAN_CARD_CLASS_NAME}
        data-testid="CurrentPlan-PlanCardLink"
        onClick={handleManageClick}
      >
        <CurrentPlanCard plan={plan} />
      </LocalizedLink>
    );
  }

  return (
    <Col className="gap-sm" data-testid="CurrentPlan">
      <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
        <Trans i18nKey="user:profile.currentPlan.title" />
      </Text>
      {planCardElement}
      <PreventManageSubscriptionModal open={stateIsPreventModalOpen} onClose={handleClosePreventModal} />
    </Col>
  );
};

export default CurrentPlan;
